import { memo } from 'react';
import { ConfirmModal } from '@/components/ConfirmModal';
import moment from 'moment';

export interface AdvisoryAckName {
  personId: number;
  firstName: string;
  lastName: string;
  acknowledgedAt: string;
  stale: boolean;
}

interface AdvisoryAckNamesModalProps {
  open: boolean;
  loading: boolean;
  error?: boolean;
  subject: string;
  names: AdvisoryAckName[];
  theme: string;
  onClose: () => void;
}

// Read-only tap-through listing who has acknowledged an urgent advisory. Shown
// to users with the update permission from the "X of Y acknowledged" affordance
// on the card. Built on ConfirmModal (cancel-only) so it inherits theming and
// focus handling without a new modal shell.
export const AdvisoryAckNamesModal = memo(function AdvisoryAckNamesModal({
  open,
  loading,
  error = false,
  subject,
  names,
  theme,
  onClose,
}: AdvisoryAckNamesModalProps) {
  return (
    <ConfirmModal
      open={open}
      title="Acknowledged by"
      confirmText=""
      cancelText="Close"
      showCancel={true}
      autoFocus="cancel"
      theme={theme}
      onConfirm={undefined}
      onCancel={onClose}
    >
      <div className="advisory-ack-names">
        {subject && <div className="advisory-ack-names__subject">{subject}</div>}
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
          </div>
        ) : error ? (
          <div className="advisory-ack-names__empty">
            Couldn't load who acknowledged this advisory. Close and reopen to try again.
          </div>
        ) : names.length === 0 ? (
          <div className="advisory-ack-names__empty">No one has acknowledged this advisory yet.</div>
        ) : (
          <ul className="advisory-ack-names__list">
            {names.map((n) => (
              <li key={n.personId} className="advisory-ack-names__item">
                <span className="advisory-ack-names__name">
                  {n.firstName} {n.lastName}
                </span>
                <span className="advisory-ack-names__when">
                  {moment(n.acknowledgedAt).format('MMM D, h:mm A')}
                  {n.stale && (
                    <span className="advisory-ack-names__stale"> · before latest edit</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ConfirmModal>
  );
});

export default AdvisoryAckNamesModal;
